/**
 * Sanitized Error Messages Utility
 * Provides user-friendly error messages without exposing sensitive data
 */

import { maskSensitiveData, maskErrorMessage } from './dataMasking'

/**
 * Error message mappings
 * Maps technical errors to user-friendly messages
 */
const ERROR_MESSAGES = {
  // Authentication errors
  'Invalid login credentials': 'Invalid email or password. Please try again.',
  'Email not confirmed': 'Please verify your email address before signing in.',
  'User already registered': 'An account with this email already exists.',
  'Invalid token': 'Your session has expired. Please sign in again.',
  'Token expired': 'Your session has expired. Please sign in again.',

  // Database errors
  'duplicate key value': 'This record already exists.',
  'foreign key constraint': 'Cannot delete this record as it is in use.',
  'not null constraint': 'Please fill in all required fields.',
  'unique constraint': 'This value already exists. Please choose another.',
  'check constraint': 'Invalid data provided. Please check your input.',

  // Network errors
  'Network request failed': 'Unable to connect to the server. Please check your internet connection.',
  'Failed to fetch': 'Unable to connect to the server. Please try again.',
  'timeout': 'The request took too long. Please try again.',

  // Permission errors
  'permission denied': 'You do not have permission to perform this action.',
  'unauthorized': 'You are not authorized to access this resource.',
  'forbidden': 'Access denied.',

  // Validation errors
  'validation error': 'Please check your input and try again.',
  'invalid input': 'Invalid data provided. Please check your input.',

  // Generic errors
  'internal server error': 'An unexpected error occurred. Please try again later.',
  'server error': 'The server encountered an error. Please try again later.',
}

/**
 * Get user-friendly error message
 */
export const getSanitizedErrorMessage = (error, options = {}) => {
  if (!error) {
    return 'An error occurred. Please try again.'
  }

  const {
    showTechnicalDetails = false,
    showStack = false,
    context = null,
  } = options

  // Get original error message
  let message = error.message || error.toString() || 'An error occurred'

  // Mask sensitive data
  message = maskSensitiveData(message, {
    patterns: ['all'],
    showFirst: 0,
    showLast: 0,
  })

  // Check for known error patterns
  const errorLower = message.toLowerCase()

  for (const [pattern, friendlyMessage] of Object.entries(ERROR_MESSAGES)) {
    if (errorLower.includes(pattern.toLowerCase())) {
      return friendlyMessage
    }
  }

  // Check for Supabase-specific errors
  if (error.code) {
    return getSupabaseErrorMessage(error.code, error.message)
  }

  // If showing technical details, include more info
  if (showTechnicalDetails) {
    return maskErrorMessage(error, {
      showStack,
      showDetails: true,
      maskUserData: true,
    })
  }

  // Default user-friendly message
  return 'An error occurred. Please try again or contact support if the problem persists.'
}

/**
 * Get Supabase-specific error messages
 */
const getSupabaseErrorMessage = (code, originalMessage) => {
  const codeMessages = {
    'PGRST116': 'The requested record was not found.',
    '23505': 'This record already exists.',
    '23503': 'Cannot perform this action as the record is in use.',
    '23502': 'Please fill in all required fields.',
    '42501': 'You do not have permission to perform this action.',
    '42P01': 'A database error occurred. Please contact support.',
    'P0001': 'A database constraint was violated. Please check your input.',
  }

  if (codeMessages[code]) {
    return codeMessages[code]
  }

  // Mask the original message
  return maskSensitiveData(originalMessage, {
    patterns: ['all'],
    showFirst: 0,
    showLast: 0,
  })
}

/**
 * Create sanitized error object
 */
export const createSanitizedError = (error, options = {}) => {
  const sanitized = {
    message: getSanitizedErrorMessage(error, options),
    code: error.code || null,
    timestamp: new Date().toISOString(),
  }

  // Include technical details only if explicitly allowed
  if (options.includeTechnicalDetails) {
    sanitized.technical = {
      originalMessage: maskSensitiveData(error.message || '', {
        patterns: ['all'],
      }),
      type: error.name || 'Error',
    }
  }

  return sanitized
}

/**
 * Sanitize error for logging
 */
export const sanitizeErrorForLogging = (error, context = {}) => {
  const sanitized = {
    message: maskSensitiveData(error.message || 'Unknown error', {
      patterns: ['all'],
    }),
    name: error.name || 'Error',
    code: error.code || null,
    context: maskSensitiveData(JSON.stringify(context), {
      patterns: ['all'],
    }),
    timestamp: new Date().toISOString(),
  }

  // Include stack trace but mask sensitive data
  if (error.stack) {
    sanitized.stack = maskSensitiveData(error.stack, {
      patterns: ['all'],
      showFirst: 0,
      showLast: 0,
    })
  }

  return sanitized
}

export default {
  getSanitizedErrorMessage,
  createSanitizedError,
  sanitizeErrorForLogging,
}

